import React, { createContext, useContext, useState } from 'react';
import loginRepo from '../repository/login';
import { message } from 'antd';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { AxiosResponse } from 'axios';
import { datadogRum } from '@datadog/browser-rum';

interface AuthContextType {
  username: string;
  completionDays: number | undefined,
  login: (username: string, password: string) => Promise<AxiosResponse<any, any> | undefined>;
  logout: () => void;
  cpf: string | undefined,
  setCpf: React.Dispatch<React.SetStateAction<string | undefined>>;
}

type Props = {
  children: React.ReactNode,
}

const AuthContext = createContext<AuthContextType | null>(null);

// Componente de provedor de autenticação
export const AuthProvider: React.FC<Props> = ({ children }) => {
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [username, setUsername] = useState<string>('');
  const [completionDays, setCompletionDays] = useState<number>();
  const [cpf, setCpf] = useState<string>();

  // Função de login
  const login = async (username: string, password: string)  => {
    try {
      const response = await loginRepo.authenticate(username.replace(/\D/g, ''), password);

      if (response.status === 200) {
        localStorage.setItem('admissao.auth.token', response.data.token);
        setUsername(response.data.name);
        setCompletionDays(response.data.totalDays);
        await verifyCaptcha();

        datadogRum.setUser({
          id: username,
          name: response.data.name,
        });

        return response;
      }
    } catch (e: any) {
      message.error(e.response.data.errors[0].message);
    }
  };

  const verifyCaptcha = async () => {
    if (executeRecaptcha) {
      for (let i = 0; i <= 10; i++) {
        try {
          const token = await executeRecaptcha('action');
          await loginRepo.validateRecaptcha(token);
          break;
        }
        catch (e) {
          continue;
        }
      }
    } else {
      console.error('Error: executeRecaptcha is undefined');
    }
  };

  const logout = () => {
    datadogRum.clearUser();
    datadogRum.stopSessionReplayRecording();
    datadogRum.stopSession();

    localStorage.removeItem('admissao.auth.token');
  };

  const authContextValue: AuthContextType = {
    username,
    completionDays,
    setCpf,
    cpf,
    login,
    logout,
  };

  return <AuthContext.Provider value={authContextValue}>{children}</AuthContext.Provider>;
};

// Hook para usar o contexto de autenticação
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};
